import { useMemo, useState } from "react"
import { Badge, Button, Checkbox, Empty, Input, Modal, Tag } from "antd"
import { Search } from "lucide-react"
import { DOMAIN_LABELS, DOMAIN_ORDER, byDomain, type MetricDefinition, type MetricDomain } from "@csbi/metric-catalog"
import { WIDGET_LABELS } from "@csbi/widgets"

export type MetricPickerProps = {
  open: boolean
  alreadyOn: ReadonlySet<string>
  onCancel: () => void
  onConfirm: (metrics: MetricDefinition[]) => void
}

export function MetricPicker({ open, alreadyOn, onCancel, onConfirm }: MetricPickerProps) {
  const [domain, setDomain] = useState<MetricDomain>(DOMAIN_ORDER[0]!)
  const [picked, setPicked] = useState<string[]>([])
  const [q, setQ] = useState("")

  const metrics = useMemo(() => {
    const all = byDomain.get(domain) ?? []
    const needle = q.trim().toLowerCase()
    return needle ? all.filter((m) => m.name.toLowerCase().includes(needle)) : all
  }, [domain, q])

  const reset = () => {
    setPicked([])
    setQ("")
  }

  const confirm = () => {
    const byIdInDomain = new Map((byDomain.get(domain) ?? []).map((m) => [m.id, m]))
    const chosen = picked
      .map((id) => byIdInDomain.get(id))
      .filter((m): m is MetricDefinition => m != null)
      .sort((a, b) => a.xlsxRow - b.xlsxRow)
    onConfirm(chosen)
    reset()
  }

  return (
    <Modal
      open={open}
      width={760}
      title="Add tiles"
      okText={picked.length > 0 ? `Add ${picked.length}` : "Add"}
      okButtonProps={{ disabled: picked.length === 0 }}
      onOk={confirm}
      onCancel={() => {
        reset()
        onCancel()
      }}
    >
      <div style={{ display: "flex", gap: 12, height: 420, marginTop: 8 }}>
        <div style={{ flex: "0 0 200px", overflowY: "auto", borderRight: "1px solid var(--csbi-gridline)" }}>
          {DOMAIN_ORDER.map((d) => {
            const n = byDomain.get(d)?.length ?? 0
            const active = d === domain
            return (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDomain(d)
                  // Changing domain clears the selection: a cross-domain selection cannot be saved (metrics in one widget must share a domain)
                  setPicked([])
                }}
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 10px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 13,
                  background: active ? "var(--csbi-gridline)" : "transparent",
                  color: active ? "var(--csbi-ink-primary)" : "var(--csbi-ink-secondary)",
                  fontWeight: active ? 600 : 400,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>{DOMAIN_LABELS[d]}</span>
                <Badge count={n} showZero color="var(--csbi-ink-muted)" />
              </button>
            )
          })}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          <Input
            size="small"
            allowClear
            value={q}
            onChange={(e) => setQ(e.target.value)}
            prefix={<Search size={13} />}
            placeholder={`Search in ${DOMAIN_LABELS[domain]}`}
          />
          <div style={{ flex: 1, overflowY: "auto" }}>
            {metrics.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No metric matches" />
            ) : (
              <Checkbox.Group
                value={picked}
                onChange={(v) => setPicked(v as string[])}
                style={{ display: "grid", gap: 2, width: "100%" }}
              >
                {metrics.map((m) => (
                  <Checkbox key={m.id} value={m.id} style={{ width: "100%", padding: "5px 6px" }}>
                    <span style={{ fontSize: 13 }}>{m.name}</span>
                    <Tag style={{ marginInlineStart: 8, marginInlineEnd: 0 }}>
                      {WIDGET_LABELS[m.defaultWidget]}
                    </Tag>
                    {alreadyOn.has(m.id) && (
                      <span style={{ marginInlineStart: 6, fontSize: 11, color: "var(--csbi-ink-muted)" }}>
                        already on this dashboard
                      </span>
                    )}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--csbi-ink-muted)" }}>
            One tile per metric, using its default chart type — you can change each one afterwards.
          </div>
        </div>
      </div>
    </Modal>
  )
}

export function AddTileButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="small" onClick={onClick}>
      Add tiles
    </Button>
  )
}
